/**
 * Aday Kayıt — Spec Bölüm 7.2 + S2.T7
 *
 * Akış:
 *   1) /sinav?code=... → erişim kodu doğrulandı → buraya gelindi
 *   2) Aday bilgilerini girer (anonim mod ise sadece anonymousId)
 *   3) POST /api/public/candidate/register → attemptId
 *   4) Redirect: /sinav/oturum/:attemptId (S2.T8'de gerçek soru UI)
 */
import { useEffect, useState } from 'react';
import { useLocation, useSearch } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CandidateRegisterSchema,
  type CandidateRegisterInput,
  type ExperienceBand,
} from '@swskills/shared';
import { Header } from '../components/Header.js';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Field,
  Input,
} from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';

interface ExamPreview {
  examId: string;
  accessCode: string;
  organization: { name: string; industry: string };
  questionCount: number;
  durationMin: number;
  anonymousMode: boolean;
  validUntil: string;
}

interface RegisterResponse {
  attemptId: string;
  resumed: boolean;
  durationMin: number;
  totalQuestions: number;
  expiresAt: string;
}

const EXPERIENCE_OPTIONS: Array<{ value: ExperienceBand; label: string }> = [
  { value: 'LT_1Y', label: '1 yıldan az' },
  { value: 'Y1_TO_2', label: '1 - 2 yıl' },
  { value: 'Y2_TO_5', label: '2 - 5 yıl' },
  { value: 'GT_5Y', label: '5+ yıl' },
];

export function CandidateRegisterPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const accessCode = (params.get('code') ?? '').toUpperCase();

  const [preview, setPreview] = useState<ExamPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (!accessCode) {
      setLocation('/sinav');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<ExamPreview>(`/api/public/exam/${accessCode}`);
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setPreviewError(err.message);
        else setPreviewError('Sınav yüklenemedi');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessCode, setLocation]);

  const isAnon = preview?.anonymousMode ?? false;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CandidateRegisterInput>({
    resolver: zodResolver(CandidateRegisterSchema),
    defaultValues: {
      accessCode,
      fullName: '',
      email: '',
      anonymousId: '',
      position: '',
      experienceYears: 'Y2_TO_5',
      publicCertConsent: false,
      kvkkAydinlatmaAccept: false as unknown as true,
      kvkkAiIslemeAccept: false as unknown as true,
    },
  });

  async function onSubmit(values: CandidateRegisterInput): Promise<void> {
    setServerError(null);
    try {
      // Anonim mod ise normal mod alanlarını temizle (Zod refine için)
      const payload: CandidateRegisterInput = isAnon
        ? {
            ...values,
            fullName: undefined,
            email: undefined,
          }
        : {
            ...values,
            anonymousId: undefined,
          };

      const data = await api.post<RegisterResponse>(
        '/api/public/candidate/register',
        payload,
      );
      setLocation(`/sinav/oturum/${data.attemptId}`);
    } catch (err) {
      if (err instanceof ApiError) {
        setServerError(err.message);
      } else {
        setServerError('Beklenmeyen bir hata oluştu');
      }
    }
  }

  if (previewError) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-md px-6 py-12">
          <Card>
            <Alert variant="danger">{previewError}</Alert>
            <Button
              onClick={() => setLocation('/sinav')}
              variant="secondary"
              className="mt-4 w-full"
            >
              Farklı bir erişim kodu gir
            </Button>
          </Card>
        </main>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-md px-6 py-12">
          <p className="text-slate-500">Sınav bilgileri yükleniyor…</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-lg px-6 py-12">
        <Card>
          <h1 className="mb-1 text-2xl font-bold text-slate-900">
            Sınava Kayıt
          </h1>
          <p className="mb-1 text-sm text-slate-600">
            <strong>{preview.organization.name}</strong> firması için
            değerlendirme.
          </p>
          <p className="mb-6 text-sm text-slate-500">
            {preview.questionCount} soru · {preview.durationMin} dakika
            {isAnon && ' · Anonim Mod (KVKK güvende)'}
          </p>

          {isAnon && (
            <div className="mb-4">
              <Alert variant="info">
                Bu sınav <strong>anonim modda</strong>. Sadece firmanızın size
                verdiği takma adı (örn. <code>ADAY-001</code>) girin. Ad, e-posta
                gibi kişisel bilgi alınmaz.
              </Alert>
            </div>
          )}

          {serverError && (
            <div className="mb-4">
              <Alert variant="danger">{serverError}</Alert>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <input type="hidden" {...register('accessCode')} value={accessCode} />

            {isAnon ? (
              <Field
                label="Anonim ID"
                htmlFor="anonymousId"
                required
                error={errors.anonymousId?.message}
                hint="Firmanızın size verdiği takma ad (örn. ADAY-001)"
              >
                <Input
                  id="anonymousId"
                  type="text"
                  error={!!errors.anonymousId}
                  {...register('anonymousId')}
                />
              </Field>
            ) : (
              <>
                <Field
                  label="Ad Soyad"
                  htmlFor="fullName"
                  required
                  error={errors.fullName?.message}
                >
                  <Input
                    id="fullName"
                    type="text"
                    autoComplete="name"
                    error={!!errors.fullName}
                    {...register('fullName')}
                  />
                </Field>

                <Field
                  label="E-posta"
                  htmlFor="email"
                  required
                  error={errors.email?.message}
                  hint="Sonuç raporu bu adrese gönderilecek"
                >
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    error={!!errors.email}
                    {...register('email')}
                  />
                </Field>
              </>
            )}

            <Field
              label="Pozisyon / Görev"
              htmlFor="position"
              error={errors.position?.message}
              hint="Opsiyonel — örn. Mekanik Tasarım Mühendisi"
            >
              <Input
                id="position"
                type="text"
                error={!!errors.position}
                {...register('position')}
              />
            </Field>

            <Field
              label="SolidWorks Deneyimi"
              htmlFor="experienceYears"
              required
              error={errors.experienceYears?.message}
            >
              <select
                id="experienceYears"
                className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                {...register('experienceYears')}
              >
                {EXPERIENCE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Field>

            <div className="mt-6 space-y-1">
              <Checkbox
                id="kvkk-aydinlatma"
                label={
                  <span>
                    <a
                      href="/kvkk"
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-900 underline"
                    >
                      KVKK Aydınlatma Metni
                    </a>
                    'ni okudum, kişisel verilerimin işlenmesini onaylıyorum.{' '}
                    <span className="text-red-500">*</span>
                  </span>
                }
                error={errors.kvkkAydinlatmaAccept?.message}
                {...register('kvkkAydinlatmaAccept')}
              />

              <Checkbox
                id="kvkk-ai"
                label={
                  <span>
                    Yanıtlarımın yapay zeka tarafından analiz edilerek
                    kişiselleştirilmiş rapor üretilmesini onaylıyorum.{' '}
                    <span className="text-red-500">*</span>
                  </span>
                }
                error={errors.kvkkAiIslemeAccept?.message}
                {...register('kvkkAiIslemeAccept')}
              />

              <Checkbox
                id="public-cert"
                label="Başarılı olursam sertifikamın LinkedIn'de paylaşılabilir kamuya açık halini onaylıyorum (opsiyonel)."
                {...register('publicCertConsent')}
              />
            </div>

            <Button type="submit" loading={isSubmitting} className="mt-4 w-full">
              Sınava Başla
            </Button>

            <p className="mt-4 text-center text-xs text-slate-500">
              "Sınava Başla"ya basarak sınav süresi başlatılır ve geri sayım çalışmaya başlar.
            </p>
          </form>
        </Card>
      </main>
    </div>
  );
}
